/**
 * disposal_report_runs — Phase 16 / Task 16.5 (DEC-162).
 *
 * Async run lifecycle for custom report generation. Backed by the
 * disposal-report sweeper (mirror of 16.4's daily-email sweeper pattern).
 *
 * Lifecycle: QUEUED (from POST /generate) → RUNNING (sweeper claim) →
 * SUCCEEDED (S3 upload + email dispatch complete) OR FAILED (any exception).
 *
 * Idempotency: the sweeper claims one QUEUED row per tick via
 * `platform_disposal_report_claim_next_queued()` with FOR UPDATE SKIP LOCKED
 * so parallel ECS ticks each pick a different row.
 *
 * template_id ON DELETE RESTRICT — templates soft-delete via archived_at;
 * a hard-delete is blocked while any run still references the template so
 * the audit trail stays intact. No DELETE grant on this table at all —
 * runs are append-and-update-status-only.
 *
 * cancelled_at reserved for a future cancel endpoint (out of scope for
 * MVP; a QUEUED run can be manually cancelled via a hypothetical DELETE
 * endpoint later without a schema migration).
 *
 * RLS: tenant-isolated via subquery to disposal_companies.tenant_id.
 * Same pattern as disposal_report_templates.
 */

import {
  check,
  date,
  index,
  integer,
  numeric,
  pgPolicy,
  pgTable,
  text,
  timestamp,
  uuid,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { appUserRole } from './enums.js';
import { disposalCompanies } from './disposals.js';
import { disposalReportTemplates } from './disposal-report-templates.js';
import { users } from './users.js';

export const disposalReportRuns = pgTable(
  'disposal_report_runs',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    disposalCompanyId: uuid('disposal_company_id')
      .notNull()
      .references(() => disposalCompanies.id, { onDelete: 'cascade' }),
    templateId: uuid('template_id')
      .notNull()
      .references(() => disposalReportTemplates.id, { onDelete: 'restrict' }),
    dateFrom: date('date_from').notNull(),
    dateTo: date('date_to').notNull(),
    status: text('status').notNull().default('QUEUED'),
    requestedByUserId: uuid('requested_by_user_id').references(() => users.id, {
      onDelete: 'set null',
    }),
    requestedAt: timestamp('requested_at', { withTimezone: true }).notNull().defaultNow(),
    startedAt: timestamp('started_at', { withTimezone: true }),
    completedAt: timestamp('completed_at', { withTimezone: true }),
    s3Bucket: text('s3_bucket'),
    s3Key: text('s3_key'),
    rowCount: integer('row_count'),
    totalBbl: numeric('total_bbl', { precision: 12, scale: 2 }),
    failureReason: text('failure_reason'),
    recipientCount: integer('recipient_count'),
    cancelledAt: timestamp('cancelled_at', { withTimezone: true }),
  },
  (t) => [
    check('disposal_report_runs_date_range_check', sql`date_to >= date_from`),
    check(
      'disposal_report_runs_status_check',
      sql`status IN ('QUEUED','RUNNING','SUCCEEDED','FAILED')`,
    ),
    index('disposal_report_runs_company_idx').on(t.disposalCompanyId, t.requestedAt.desc()),
    index('disposal_report_runs_queued_idx')
      .on(t.status)
      .where(sql`status IN ('QUEUED','RUNNING')`),
    index('disposal_report_runs_template_idx').on(t.templateId),
    pgPolicy('disposal_report_runs_tenant_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`disposal_company_id IN (
        SELECT dc.id FROM disposal_companies dc
        WHERE dc.tenant_id::text = current_setting('app.current_tenant_id', true)
      )`,
    }),
  ],
);

export type DisposalReportRun = typeof disposalReportRuns.$inferSelect;
export type NewDisposalReportRun = typeof disposalReportRuns.$inferInsert;
export const disposalReportRunSelectSchema = createSelectSchema(disposalReportRuns);
export const disposalReportRunInsertSchema = createInsertSchema(disposalReportRuns);
